import React, { useEffect, useRef, useState } from 'react';
import Chart, { Plugin } from 'chart.js/auto';
import { ScaleOptions, DataSet, Tooltip } from './types';
import { lineThroughPointsPlugin, summaryLabelsPlugin } from './plugins';

import { charts } from '@ecosystem/newron-design-system';
import { FeatureToggleButton, useFeatureToggle } from '../../tools/featureToggle';
import { LineChartGroup } from '@ecosystem/newron-design-system/build/exports/datavis';
import { getMainGraphWidth } from '../../common/util';
import { ContextType, renderTooltip } from './tooltip';
import { getChartHeight } from './utils';

const { LineChart: VisxLineChart, ChartScaleType, LegendPosition } = charts;

export type LineData = {
  labels: (string[] | string)[];
  sets: DataSet[];
  summary?: DataSet;
};

export type LineChartOptions = {
  xScale?: ScaleOptions;
  yScale?: ScaleOptions;
  data?: LineData;
  tooltip?: Tooltip;
  compactView?: boolean;
};

type Props = {
  options: LineChartOptions;
  trackTooltipEvent: () => void;
  isMobile: boolean;
  isChartFullView: boolean;
  chartHeightOffset?: number;
};

function LineChart({
  options = {},
  trackTooltipEvent,
  isMobile,
  isChartFullView,
  chartHeightOffset = 0,
}: Readonly<Props>) {
  const [width, setWidth] = useState<string>();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const optionsRef = useRef<string>(null);
  const visxOptionsRef = useRef<string>(null);

  const [chartData, setChartdata] = useState<LineChartGroup[]>();
  const { xScale, yScale, data, tooltip, compactView } = options;
  const { value: useVisx, toggleFeatureTag } = useFeatureToggle('visx-linechart');
  const { value: useToggleButton } = useFeatureToggle('visx-linechart-toggle');

  const tooltipFormat = item => {
    const formattedValue = tooltip?.valueFormatter ? tooltip.valueFormatter(Number(item.raw)) : item.formattedValue;
    return `${item.dataset.label}: ${formattedValue}`;
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (useVisx) {
      return;
    }

    if (!canvasRef.current || !data || optionsRef.current === JSON.stringify(options)) {
      return;
    }

    const { sets, labels, summary } = data;

    optionsRef.current = JSON.stringify(options);

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    if (compactView) {
      setWidth('100%');
    } else {
      const chartWidth = getMainGraphWidth(data, true);
      setWidth(`${chartWidth}px`);
    }

    const plugins: Plugin[] = [lineThroughPointsPlugin()];
    if (summary && !compactView) {
      plugins.push(summaryLabelsPlugin(summary));
    }

    let hoverTimeout;

    chartRef.current = new Chart(canvasRef.current, {
      plugins: plugins,
      type: 'line',
      data: {
        labels,
        datasets: sets.map(set => ({
          label: set.label,
          data: set.values.raw,
          borderColor: set.values.fillColor,
          backgroundColor: set.values.fillColor,
          pointRadius: compactView ? 2 : undefined,
          spanGaps: true,
          segment: {
            borderDash: ctx => (ctx.p0.skip || ctx.p1.skip ? [4, 4] : undefined),
            borderWidth: ctx => (ctx.p0.skip || ctx.p1.skip ? 1 : undefined),
          },
        })),
      },
      options: {
        onHover: (_event, _activeElements, chart) => {
          clearTimeout(hoverTimeout);
          hoverTimeout = setTimeout(() => {
            const activeElements = chart.getActiveElements();
            if (activeElements.length > 0) {
              trackTooltipEvent();
            }
          }, 1000);
        },
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: !summary ? 0 : 65,
            right: !summary ? 0 : 50,
          },
        },
        plugins: {
          tooltip: {
            callbacks: {
              label: tooltipFormat,
            },
            enabled: false, // Disable default tooltip rendering of linechart

            external: function (lContext: ContextType) {
              //render custom tooltip for line chart
              renderTooltip(lContext);
            },
          },
          legend: {
            display: false,
          },
        },
        interaction: {
          mode: 'nearest',
          intersect: false,
        },
        scales: {
          x: {
            border: {
              display: false,
            },
            grid: {
              display: true,
            },
            title: {
              display: xScale?.title !== undefined,
              text: xScale?.title || '',
            },
            ticks: {
              callback: function (value: string | number, index: number) {
                if (compactView && index % 4 !== 0) {
                  return '';
                }
                if (xScale?.formatter) {
                  return xScale.formatter(value);
                } else {
                  return labels[index];
                }
              },
              autoSkip: false,
              maxRotation: 0,
            },
          },
          y: {
            border: {
              display: false,
            },
            grid: {
              display: true,
            },
            title: {
              display: yScale?.title !== undefined,
              text: yScale?.title || '',
              align: 'end',
            },
            beginAtZero: true,
            ticks: {
              display: yScale?.displayTicks === false ? false : true,
              callback: function (value: string | number) {
                if (yScale?.formatter) {
                  return yScale.formatter(value);
                } else {
                  return value;
                }
              },
            },
            grace: 0,
          },
        },
      },
    });

    return () => {
      clearTimeout(hoverTimeout);
    };
  }, [options, useVisx]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!useVisx) {
      return;
    }

    if (!data || visxOptionsRef.current === JSON.stringify(options)) {
      return;
    }

    const { sets } = data;

    visxOptionsRef.current = JSON.stringify(options);

    const lineChartData = sets
      .filter(set => set.values.raw.length > 0)
      .map(set => {
        return {
          groupName: set.label,
          data: set.values.raw
            .map((value, idx) => ({
              x: idx,
              y: value,
            }))
            .filter(point => point.y !== null),
        };
      });
    setChartdata(lineChartData);
  }, [options, useVisx]);

  const formatLabel = (label: string | string[], formatter?: (value: string | number) => string) => {
    const labelParts = Array.isArray(label) ? label : [label];
    return labelParts.map(labelPart => (formatter ? formatter(labelPart) : labelPart)).join(' ');
  };

  return (
    <div
      data-test-id="line-chart"
      style={{
        width,
        height: getChartHeight(isMobile, isChartFullView, false, chartHeightOffset),
      }}
    >
      {useToggleButton && (
        <FeatureToggleButton featureTag="visx-linechart" tagOn={useVisx} toggleFeatureTag={toggleFeatureTag} />
      )}
      {<canvas className="h-full" ref={canvasRef} style={{ display: useVisx ? 'none' : 'block' }} />}
      {useVisx && chartData && (
        <VisxLineChart
          data={chartData}
          fillContainer={true}
          height={500}
          width={2000} // TODO: refactor when sizing bug is fixed (use fillContainer instead, but it doesn't work now)
          xScaleType={ChartScaleType.Linear}
          yScaleType={ChartScaleType.Linear}
          tooltipProps={{
            header: (points, xKey) => {
              const idx = points[0][xKey];
              const label = data.labels[idx];
              return formatLabel(label, options.xScale?.formatter);
            },
            valueFormat: options.tooltip.valueFormatter,
          }}
          axesConfig={{
            xBottom: {
              label: xScale?.title,
              hideAxisLine: true,
              hideZero: false,
              tickSpacing: 130,
              tickLabelClassName: 'text-sm',
              tickFormat: idx => {
                const label = data.labels[idx];
                return formatLabel(label, options.xScale?.formatter);
              },
              hideTicks: !xScale?.displayTicks,
            },
            yLeft: {
              label: yScale?.title,
              hideAxisLine: true,
              hideZero: false,
              tickLabelClassName: 'text-sm',
              tickFormat: value => {
                if (yScale?.displayTicks === false) {
                  return '';
                }
                return options.yScale?.formatter ? options.yScale.formatter(value) : value;
              },
              hideTicks: !yScale?.displayTicks,
            },
          }}
          showMarkers={!compactView}
          isColumnGridShown={false}
          customLineColors={data.sets.map(set => set.values.fillColor[0])}
          legendPosition={LegendPosition.Hide}
        />
      )}
    </div>
  );
}

export { LineChart };
